import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectsBase } from 'clicklease-ui-elements';
import { catchError, concatMap, exhaustMap, first, groupBy, map, mergeMap, of, switchMap } from 'rxjs';

import { PaymentMethodRequestService } from '../../services/payment/paymentMethodRequest.service';
import * as PaymentMethodActions from './payment-method.actions';

/**
 * Backup payment method side effects (OPS-2028).
 */
@Injectable()
export class PaymentMethodEffects extends EffectsBase {

    readonly #paymentMethodRequestService: PaymentMethodRequestService = inject(PaymentMethodRequestService);

    /**
     * A ROSE with no card on file is a normal state, so an empty 204 body resolves to null
     * rather than erroring.
     *
     * Keyed per lease rather than serialised: two leases can be open for lookup at once — the
     * modal asking about one while another is still resolving — and `switchMap` would cancel
     * all but the last, leaving the map describing only whichever was asked about most
     * recently. `groupBy` lets distinct leases resolve concurrently while `exhaustMap` still
     * collapses a duplicate ask for the same one.
     */
    loadBackupMethodEffect = this.createEffect(() =>
        this.actions.pipe(
            ofType(PaymentMethodActions.loadBackupMethod),
            groupBy(({ leaseKey }) => leaseKey),
            mergeMap((leaseRequests) =>
                leaseRequests.pipe(
                    exhaustMap(({ leaseKey }) =>
                        this.#paymentMethodRequestService.getBackupPaymentMethod(leaseKey).pipe(
                            first(),
                            map((backupMethod) => PaymentMethodActions.loadBackupMethodSuccess({
                                leaseKey,
                                backupMethod: backupMethod ?? null
                            })),
                            catchError((error) =>
                                of(PaymentMethodActions.loadBackupMethodError({
                                    error: error?.error?.message || 'Unable to load your payment methods'
                                })))
                        )
                    )
                )
            )
        )
    );

    openTokenizeSessionEffect = this.createEffect(() =>
        this.actions.pipe(
            ofType(PaymentMethodActions.openTokenizeSession),
            switchMap(({ leaseKey }) =>
                this.#paymentMethodRequestService.openTokenizeSession(leaseKey).pipe(
                    first(),
                    map((session) => PaymentMethodActions.openTokenizeSessionSuccess({ session })),
                    catchError((error) =>
                        of(PaymentMethodActions.openTokenizeSessionError({
                            error: error?.error?.message || 'Unable to load the card form'
                        })))
                )
            )
        )
    );

    /**
    /**
     * One call: rose-portal-service persists the method and the authorization onto the same
     * payment-service row.
     *
     * This used to be a save followed by a separate authorize. The two were not atomic, so a
     * failed second call left the card stored with no consent recorded while the ROSE was told
     * her card could not be saved - and retrying re-posted a token that was already on file.
     */
    saveBackupMethodEffect = this.createEffect(() =>
        this.actions.pipe(
            ofType(PaymentMethodActions.saveBackupMethod),
            concatMap(({ leaseKey, token, authorizationDescription }) =>
                this.#paymentMethodRequestService
                    .saveBackupPaymentMethod(leaseKey, token, authorizationDescription)
                    .pipe(
                        first(),
                        map(() => PaymentMethodActions.saveBackupMethodSuccess({
                            leaseKey,
                            backupMethod: {
                                paymentType: token.paymentType,
                                lastFour: token.lastFour,
                                cardBrand: token.cardBrand ?? null
                            }
                        })),
                        catchError((error) =>
                            of(PaymentMethodActions.saveBackupMethodError({
                                error: error?.error?.message || 'We could not save your card. Please try again.'
                            })))
                    )
            )
        )
    );

}
